import { cn, useTranslation } from '@hbs-us/ui';
import type { ReactNode } from 'react';

/**
 * One card on a Home layout: a heading, an optional line under it, an optional action beside it,
 * and a body the card only reaches when it has something to put there.
 *
 * IT EXISTS BECAUSE THE FRAME WAS ABOUT TO BE COPIED SIX TIMES, and it now owns the STATES for the
 * same reason. Every card that fetches its own data wrote the identical three-branch ternary —
 * loading, then a red line, then "nothing to show" — and the copies had already drifted: none of
 * them offered a way to try again, so a card that failed once stayed failed until the whole screen
 * was reloaded. Six copies of a branch is six places for the next fix to miss.
 *
 * CHILDREN RENDER ONLY WHEN THE CARD IS READY. That is the point of taking `status` rather than
 * leaving each caller to guard its own body: a card cannot half-render a list from a response that
 * never arrived, and a caller cannot forget one of the three branches.
 *
 * `h-full` and the column layout are the part that matters visually. Cards sit in a grid row, and a
 * row is as tall as its tallest card, so every card must stretch to it — otherwise a short card
 * floats and the headings and links across a row no longer sit on the same lines.
 *
 * The action is a node rather than a route, so the caller keeps the router's typed `to`/`search`
 * checking. Passing a path through here would trade that away for nothing.
 */

/**
 * What the card has to show.
 *
 * `ready` is the default so a card fed from props rather than from a request — the owner cards read
 * their numbers off the single `/overview` response — says nothing about states it cannot be in.
 */
export type HomeCardStatus = 'pending' | 'error' | 'empty' | 'ready';

/**
 * The state a card is in, read off its own query.
 *
 * The three branches were written out by hand in five cards and are the whole reason this shell
 * exists; deriving them in one place is what stops a sixth card inventing a fourth answer. `rows`
 * is what the card would actually LIST, after its own filtering and slicing — a response that
 * arrived carrying only closed work is empty to the person reading it.
 */
export function homeCardStatus(
  query: { isPending: boolean; isError: boolean },
  rows: number,
): HomeCardStatus {
  if (query.isPending) return 'pending';
  if (query.isError) return 'error';
  return rows === 0 ? 'empty' : 'ready';
}

export function HomeCard({
  headingId,
  title,
  description,
  action,
  status = 'ready',
  errorText,
  emptyText,
  onRetry,
  className,
  children,
}: {
  headingId: string;
  title: string;
  /** A quiet second line under the heading. Used where a card's subject needs naming, not padding. */
  description?: string;
  action?: ReactNode;
  status?: HomeCardStatus;
  /** What failed, in this card's own words. A shared sentence would name nothing. */
  errorText?: string;
  emptyText?: string;
  /** Offered only when the caller can actually retry — usually a query's own `refetch`. */
  onRetry?: () => void;
  className?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <section
      aria-labelledby={headingId}
      // Matches `StatCard` (packages/ui/src/StatCard.tsx:70-73) exactly. The KPI band and these
      // cards share a viewport, and a 6px radius with no elevation beside a 12px one with
      // `shadow-elev-1` read as two component kits on one screen.
      className={cn(
        'flex h-full flex-col rounded-xl border border-border bg-card p-5 shadow-elev-1',
        className,
      )}
    >
      {/*
        `items-start`, not baseline: the action slot carries a link on most cards and a whole block
        on two of them (the receivables status strip, the revenue figure). Both sides are `text-sm`
        with the same line box, so a link still lands on the heading's baseline; a block no longer
        hangs off it. `flex-wrap` is what stops a wide action crushing the heading on a phone.
      */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id={headingId} className="font-semibold text-foreground text-sm">
            {title}
          </h2>
          {description ? (
            <p className="mt-0.5 text-muted-foreground text-xs">{description}</p>
          ) : null}
        </div>
        {action}
      </div>

      {status === 'pending' ? (
        <p className="mt-3 text-muted-foreground text-sm">{t('common:status.loading')}</p>
      ) : status === 'error' ? (
        // One live region, so a screen reader hears the sentence and the way out together rather
        // than announcing a bare failure and leaving the button to be found.
        <div role="alert" className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-destructive text-sm">{errorText}</span>
          {onRetry ? (
            <button type="button" onClick={onRetry} className={HOME_CARD_RETRY}>
              {t('common:error.retry')}
            </button>
          ) : null}
        </div>
      ) : status === 'empty' ? (
        <p className="mt-3 text-muted-foreground text-sm">{emptyText}</p>
      ) : (
        children
      )}
    </section>
  );
}

/**
 * The shared treatment for a card's "open the real screen" link.
 *
 * `text-ring`, not `text-primary`. --primary is the vivid signal orange and measures 3.12:1 on the
 * light card, below the 4.5:1 AA floor for 14px text. --ring is the darker orange step in light and
 * the vivid one on the dark near-black, so one utility clears AA in BOTH themes. (Values named, not
 * written: the BRAND-06 gate rejects a hex literal in src/features/** even inside a comment.)
 */
export const HOME_CARD_LINK =
  'font-medium text-ring text-sm underline-offset-4 transition-[color,transform] duration-[var(--dur-quick)] ease-[var(--ease-out)] hover:underline active:scale-[0.96] motion-reduce:transition-none motion-reduce:active:scale-100 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card';

/**
 * The retry beside a failed card. Ink rather than the accent: it sits next to the destructive
 * sentence it belongs to, and a second coloured word there would compete with the failure itself.
 * The same treatment the punch screen's week ledger already uses for the same job.
 */
const HOME_CARD_RETRY =
  'font-medium text-foreground text-sm underline underline-offset-4 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card';

/** The tinted row used for a list item inside a card, so every list on Home reads the same. */
export const HOME_CARD_ROW = 'rounded-md bg-secondary/40 px-3 py-2';
